import { CAEPF_ACTIVE_STATUS, type CaepfEntry } from '@/services';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Situação cadastral regular — só CAEPF ATIVO pode ser usado na cotação. */
export function isActiveCaepf(status: string | null | undefined): boolean {
  return (status ?? '').trim().toUpperCase() === CAEPF_ACTIVE_STATUS;
}

/** Aviso de CAEPF com situação cadastral irregular (exibido em vermelho). */
export function buildCaepfInactiveMessage(
  status: string | null | undefined
): string {
  const label = (status ?? '').trim() || 'IRREGULAR';
  return (
    `O CAEPF informado consta como "${label}" na Receita Federal. ` +
    'Para prosseguir, é necessário que esteja com situação ATIVA.'
  );
}

/**
 * O que fazer com o resultado de uma consulta CPF/CAEPF.
 *
 * - `idle`      — sem documento consultado (ou consulta ainda sem resultado)
 * - `direct`    — CAEPF digitado direto (14 díg.): preenche sem modal
 * - `select`    — CPF (11 díg.) com 1+ CAEPFs vinculados: abre o modal
 * - `no-caepf`  — CPF (11 díg.) sem nenhum CAEPF: aviso, sem modal
 */
export type CaepfLookupOutcome = 'idle' | 'direct' | 'select' | 'no-caepf';

type ResolveParams = {
  /** Documento consultado, somente dígitos. */
  cleanDocument: string;
  /** CAEPFs devolvidos pela consulta. */
  items?: CaepfEntry[] | null;
};

/**
 * Decide o desfecho de uma consulta a partir do documento e dos CAEPFs
 * retornados. Função pura — o hook abaixo só orquestra estado em cima dela.
 */
export function resolveCaepfLookupOutcome({
  cleanDocument,
  items,
}: ResolveParams): CaepfLookupOutcome {
  const digits = (cleanDocument ?? '').replace(/\D/g, '');

  if (digits.length === 14) return 'direct';
  if (digits.length !== 11) return 'idle';

  // A partir daqui é um CPF: só existe desfecho quando a consulta respondeu.
  if (!items) return 'idle';

  return items.length > 0 ? 'select' : 'no-caepf';
}

/** Aviso exibido quando o CPF consultado não possui CAEPF. */
export const CAEPF_NOT_FOUND_MESSAGE =
  'O CPF informado não possui CAEPF na Receita Federal. ' +
  'Confira o documento ou informe o CAEPF diretamente.';

type UseCaepfSelectionParams = {
  /**
   * Aplica o CAEPF escolhido no formulário do chamador. `consultedCpf` é o CPF
   * (11 díg.) que originou a consulta — o chamador o guarda separado para não
   * perder o CPF quando o CAEPF sobrescreve o `documentNumber`. Vazio quando o
   * CAEPF foi digitado direto (14 díg.).
   */
  onSelect: (entry: CaepfEntry, consultedCpf: string) => void;
};

/**
 * Estado do modal de seleção de CAEPF.
 *
 * Guarda o último documento já tratado para não reabrir o modal a cada
 * re-render/refetch do react-query — o resultado da consulta é estável e o
 * efeito do chamador dispara várias vezes para o mesmo documento.
 */
export function useCaepfSelection({ onSelect }: UseCaepfSelectionParams) {
  const [pending, setPending] = useState<{ items: CaepfEntry[]; document: string } | null>(null);
  const handledDocumentRef = useRef<string | null>(null);
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  /**
   * Aplica o resultado da consulta por CPF. Idempotente por documento: só age
   * uma vez até que o documento consultado mude.
   *
   * Com um ÚNICO CAEPF não há escolha a fazer — aplica direto, sem abrir o
   * modal (mesmo que a situação cadastral seja irregular; nesse caso quem
   * avisa é a tela, com a mensagem em vermelho abaixo do campo). O modal só
   * aparece quando há mais de um CAEPF vinculado ao CPF.
   */
  const requestSelection = useCallback(
    (document: string, items: CaepfEntry[]) => {
      if (!document || items.length === 0) return;
      if (handledDocumentRef.current === document) return;

      handledDocumentRef.current = document;

      if (items.length === 1) {
        onSelectRef.current(items[0], document);
        return;
      }

      setPending({ items, document });
    },
    []
  );

  /**
   * Libera o documento para uma nova abertura. Chamar quando o campo é limpo
   * ou o corretor digita outro documento — sem isso, voltar ao mesmo CPF não
   * reabriria o modal.
   */
  const resetFor = useCallback((document: string) => {
    if (handledDocumentRef.current !== document) {
      handledDocumentRef.current = null;
    }
  }, []);

  const close = useCallback(() => setPending(null), []);

  const confirm = useCallback(
    (entry: CaepfEntry) => {
      onSelectRef.current(entry, pending?.document ?? '');
      setPending(null);
    },
    [pending]
  );

  return {
    isOpen: pending !== null,
    items: pending?.items ?? [],
    requestSelection,
    resetFor,
    close,
    confirm,
  };
}
